<script setup lang="ts">
import { computed, type PropType } from 'vue';
import GaiaText from './GaiaText.vue';
import { gaiaNeutral } from '../themes/tokens';

const props = defineProps({
  /** Field label, e.g. "Type", "Tags", "Owner". */
  label: {
    type: String,
    required: true,
  },
  /** uppercase = the Contract Detail info card; plain = the entity detail grid
      (dense/600 tertiary label, wider column); stacked = label above value, for
      a cell in a summary grid (recipient card, request overview, run summary);
      table = the Calendar Detail configuration card, where the label is a
      filled cell on a tinted ground rather than a word beside the value. */
  variant: {
    type: String as PropType<'uppercase' | 'plain' | 'stacked' | 'table'>,
    default: 'uppercase',
  },
  /** Label column width in px (defaults: 112 uppercase, 220 plain, 140–180 table).
      Ignored when stacked. */
  labelWidth: {
    type: Number,
    default: undefined,
  },
  /** Hairline under the row; turn off for rows inside cards. Stacked never draws one. */
  divider: {
    type: Boolean,
    default: true,
  },
  /**
   * Step the cell down to the panel scale — a `nano` label over a `dense`
   * value. For a grid inside an already-dense card (the playbook wizard's
   * clause attributes, which sit under a `nano` panel label beside `dense`
   * body copy) rather than a page-level summary, where the default reads right.
   */
  dense: {
    type: Boolean,
    default: false,
  },
});

const isPlain = computed(() => props.variant === 'plain');
const isStacked = computed(() => props.variant === 'stacked');
const isTable = computed(() => props.variant === 'table');

const labelSize = computed(() => {
  if (isPlain.value || isTable.value) return 'dense';
  return props.dense ? 'nano' : 'micro';
});
const valueSize = computed(() => {
  if (isTable.value) return 'dense';
  return props.dense ? 'dense' : 'body';
});

// The table variant's label column breathes rather than sitting at a fixed
// width: its cells are filled, so a column sized for the longest label would
// leave a wide band of tint beside the short ones.
const labelColumn = computed(() => {
  if (props.labelWidth !== undefined) return `${props.labelWidth}px`;
  if (isTable.value) return 'minmax(140px, 180px)';
  return `${props.variant === 'plain' ? 220 : 112}px`;
});

// The table variant's value column may be narrow — two of these sit side by
// side in the configuration card — so it asks for nothing and wraps instead.
const valueColumn = computed(() => (isTable.value ? 'minmax(0, 1fr)' : 'minmax(200px, 1fr)'));

const rowBorder = gaiaNeutral.borderSoft;
const cellTint = gaiaNeutral.surfaceSoft;
const cellBorder = gaiaNeutral.borderSubtle;
</script>

<template>
  <!-- Definition row from the Contract Detail info card: uppercase label
       column + value. Naive's n-descriptions can't express this typography,
       so it's self-contained on the shared tokens. -->
  <div
    class="gaia-field-row"
    :class="[`gaia-field-row--${props.variant}`, { 'no-divider': !props.divider }]"
  >
    <gaia-text
      class="label"
      :variant="isPlain || isTable ? 'default' : 'label'"
      :size="labelSize"
      :tone="isPlain || isTable ? 'tertiary' : isStacked ? 'subtle' : 'faint'"
      :weight="isPlain ? 'semibold' : undefined"
      :style="{ paddingTop: isPlain || isStacked || isTable ? '0' : '2px' }"
    >
      {{ props.label }}
    </gaia-text>
    <gaia-text
      class="value"
      :size="valueSize"
      :tone="isStacked || isTable ? 'ink' : 'text'"
      :style="{ lineHeight: 1.55 }"
    >
      <slot />
    </gaia-text>
  </div>
</template>

<style scoped>
/* A two-column definition grid: a fixed label column plus a value column with
   its own minimum. n-grid is span-based with uniform fractions and can't
   express `<px> minmax(200px, 1fr)`, so the template stays CSS. */
.gaia-field-row {
  display: grid;
  grid-template-columns: v-bind(labelColumn) v-bind(valueColumn);
  gap: 20px;
  align-items: start;
  padding: 11px 0;
  border-bottom: 1px solid v-bind(rowBorder);
}

.gaia-field-row--plain {
  gap: 24px;
  padding: 14px 0;
}

/* Stacked: one column, so it fits a narrow grid cell where the two-column form
   would overrun its track (its value column alone asks for 200px). The value
   breaks inside a word rather than pushing the cell wide — emails and file
   names have no spaces to break at. */
/* Stays CSS: this is the variant that switches the row's `display` off the base
   rule's grid, and an n-flex writes `display` inline for every variant. */
.gaia-field-row--stacked {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  padding: 0;
  border-bottom: none;
}

.gaia-field-row--stacked .value {
  overflow-wrap: anywhere;
}

/* Table: the label stops being a word beside the value and becomes a filled
   cell — a tinted track with its own hairline, the way the mock's configuration
   card reads. The cells own the padding, so the row itself has none, and they
   stretch to the row's height or the tint would stop short of the divider. */
.gaia-field-row--table {
  gap: 0;
  padding: 0;
  align-items: stretch;
  border-bottom-color: v-bind(cellBorder);
}

.gaia-field-row--table .label {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  background: v-bind(cellTint);
  border-right: 1px solid v-bind(cellBorder);
}

.gaia-field-row--table .value {
  padding: 12px 16px;
  /* Emails, file names and tokens have no spaces to break at, and a value cell
     here is only ever a fraction of the card. */
  overflow-wrap: anywhere;
}

.no-divider {
  padding: 4px 0;
  border-bottom: none;
}

.value {
  min-width: 0;
}
</style>
